// Bolla "Messaggi" nel topbar = SOLO chat di team (conversazioni interne).
// Badge = somma conversazioni non lette (conversation.unread_count, unica fonte
// di verità per le chat). Le notifiche evento/commenti stanno nel campanello.
import { useEffect, useRef, useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { useConversations } from '../hooks/useConversations'
import type { ConversationRow } from '../lib/api'

function relativeTime(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  const diffMs = Date.now() - d.getTime()
  const min = Math.floor(diffMs / 60_000)
  if (min < 1) return 'ora'
  if (min < 60) return `${min} min fa`
  const hr = Math.floor(min / 60)
  if (hr < 24) return `${hr}h fa`
  const day = Math.floor(hr / 24)
  if (day === 1) return 'ieri'
  if (day < 7) return `${day}g fa`
  const dd = String(d.getDate()).padStart(2, '0')
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  return `${dd}/${mm}`
}

export function MessagesInbox() {
  const { data } = useConversations()
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function onClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    if (open) document.addEventListener('mousedown', onClickOutside)
    return () => document.removeEventListener('mousedown', onClickOutside)
  }, [open])

  const conversations: ConversationRow[] = data?.items ?? []
  const unread = conversations.reduce((n, c) => n + (c.unread_count || 0), 0)
  const badge = unread > 99 ? '99+' : String(unread)

  // Dropdown: solo chat con messaggi, non lette in cima, poi per data desc.
  const recent = conversations
    .filter((c) => c.last_message_at)
    .sort((a, b) => {
      const au = a.unread_count > 0 ? 1 : 0
      const bu = b.unread_count > 0 ? 1 : 0
      if (au !== bu) return bu - au
      return (
        new Date(b.last_message_at as string).getTime() -
        new Date(a.last_message_at as string).getTime()
      )
    })
    .slice(0, 12)

  function openConversation(c: ConversationRow) {
    setOpen(false)
    void navigate({ to: '/messaggi', search: { conversation: c.id } as never })
  }

  return (
    <div className="bell-wrap" ref={ref}>
      <button
        type="button"
        className="iconbtn"
        onClick={() => setOpen((o) => !o)}
        aria-label="Chat team"
        title="Chat di team"
      >
        <span style={{ fontSize: 17, lineHeight: 1 }} aria-hidden="true">
          🗨️
        </span>
        {unread > 0 && <span className="num-badge">{badge}</span>}
      </button>
      {open && (
        <div className="notif-dropdown">
          {recent.length === 0 ? (
            <div className="notif-empty">Nessuna chat</div>
          ) : (
            <>
              {recent.map((c) => (
                <div
                  key={c.id}
                  className={`notif-item ${c.unread_count > 0 ? 'unread' : ''}`}
                  onClick={() => openConversation(c)}
                >
                  <div className="notif-icon">🗨️</div>
                  <div className="notif-content">
                    <div
                      className="notif-title"
                      style={c.unread_count > 0 ? { fontWeight: 700 } : undefined}
                    >
                      {c.title}
                      {c.unread_count > 0 && (
                        <span
                          className="num-badge"
                          style={{
                            position: 'static',
                            marginLeft: 6,
                            display: 'inline-block',
                          }}
                        >
                          {c.unread_count}
                        </span>
                      )}
                    </div>
                    {c.last_message_preview && (
                      <div className="notif-body">{c.last_message_preview}</div>
                    )}
                    <div className="notif-time">
                      {c.last_message_at ? relativeTime(c.last_message_at) : ''}
                    </div>
                  </div>
                </div>
              ))}
              <div className="notif-footer">
                <button
                  type="button"
                  className="link-btn"
                  onClick={() => {
                    setOpen(false)
                    void navigate({ to: '/messaggi', search: {} as never })
                  }}
                >
                  Apri tutte le chat →
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  )
}
